import { useState } from 'react'
import Ladehinweis from '../../komponenten/Ladehinweis'
import { useLaden } from '../../lib/laden'
import { holePruefstand, holePunkteabgleich } from '../../lib/kennzahlen'
import { ApiFehler } from '../../lib/api'

/**
 * Der Prüfstand: Stimmt der Bestand noch mit dem überein, was das Schema
 * behauptet?
 *
 * Eine Tabelle, keine Kacheln, Auffällige oben. Ein Prüfstand, auf dem man
 * scrollen muss, um das Rote zu finden, ist ein Zierdeckchen.
 */
export default function Pruefstand() {
  const [zustand] = useLaden(holePruefstand)

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />
  const { pruefungen, dauerMs } = zustand.wert
  const auffaellig = pruefungen.filter((p) => p.urteil !== 'ok')

  return (
    <section className="stapel">
      <div className="stapel-kopf">
        <h1>Prüfstand</h1>
        <p className="gedaempft zahl">
          {auffaellig.length === 0
            ? `${pruefungen.length} Prüfungen, alle in Ordnung`
            : `${auffaellig.length} von ${pruefungen.length} auffällig`}
          <span className="leise"> · {Math.round(dauerMs)} ms</span>
        </p>
      </div>

      <p className="gedaempft">
        Jede Migration endet mit einer Gegenprobe. <strong>Alle Sollwerte
        sind 0</strong> — eine Prüfung, deren Sollwert man sich merken muss,
        wird falsch gelesen.
      </p>

      <div className="karte">
        <div className="tabelle-huelle">
          <table className="tabelle pruefstand-tabelle">
            <thead>
              <tr>
                <th>Prüfung</th>
                <th>Ist</th>
                <th>Soll</th>
                <th>Herkunft</th>
              </tr>
            </thead>
            <tbody>
              {pruefungen.map((p) => (
                <tr key={p.pruefung} className={p.urteil === 'ok' ? '' : 'anders'}>
                  <td>
                    <span className={`punkt punkt-${p.urteil === 'ok' ? 'gruen' : 'rot'}`} aria-hidden="true" />
                    {p.pruefung}
                  </td>
                  <td className={p.urteil === 'ok' ? 'zahl' : 'zahl wort-ab'}>{p.ist}</td>
                  <td className="zahl leise">0</td>
                  <td className="leise">{p.herkunft}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Punkteabgleich />
      <AuthAbgleich />
    </section>
  )
}

/** Die teure Prüfung — sie liest drei Tabellen vollständig, also auf Zuruf. */
function Punkteabgleich() {
  const [zustand, setZustand] = useState<
    | { art: 'ruht' }
    | { art: 'laeuft' }
    | { art: 'da'; abweichungen: number; dauerMs: number }
    | { art: 'fehler'; meldung: string }
  >({ art: 'ruht' })

  async function starten() {
    setZustand({ art: 'laeuft' })
    try {
      const w = await holePunkteabgleich()
      setZustand({ art: 'da', ...w })
    } catch (f) {
      setZustand({
        art: 'fehler',
        meldung: f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler',
      })
    }
  }

  return (
    <div className="karte">
      <h2>Punkteabgleich</h2>
      <p className="gedaempft">
        Stimmt <span className="zahl">core_app_user.points</span> mit der Summe
        aus allen Beitragszeilen überein? Steht dort etwas anderes als 0, hat
        jemand die Punkte direkt geschrieben — der teuerste stille Fehler, den
        dieses Schema kennt.
      </p>
      <p className="leise">
        Liest drei Tabellen vollständig und läuft deshalb nur auf Knopfdruck.
      </p>

      {zustand.art === 'da' && (
        <p
          className={
            zustand.abweichungen === 0 ? 'meldung meldung-erfolg' : 'meldung meldung-fehler'
          }
          role={zustand.abweichungen === 0 ? 'status' : 'alert'}
        >
          {zustand.abweichungen === 0
            ? 'Kein Konto weicht ab.'
            : `${zustand.abweichungen} Konten weichen ab.`}
          <span className="leise"> · {Math.round(zustand.dauerMs)} ms</span>
        </p>
      )}
      {zustand.art === 'fehler' && (
        <p className="meldung meldung-fehler" role="alert">
          {zustand.meldung}
        </p>
      )}

      <div className="knopf-reihe">
        <button
          className="knopf knopf-haupt"
          type="button"
          disabled={zustand.art === 'laeuft'}
          onClick={() => void starten()}
        >
          {zustand.art === 'laeuft' ? 'Rechnet …' : 'Punkte abgleichen'}
        </button>
      </div>
    </div>
  )
}

/**
 * Die Prüfung, die dieser Bildschirm **nicht** kann — und die deshalb
 * dastehen muss.
 *
 * Der Abgleich mit Firebase Authentication braucht den Auth-Export, den SQL
 * nicht sieht. Eine fehlende Prüfung, die sich als bestanden ausgibt, wäre
 * schlimmer als eine fehlende.
 */
function AuthAbgleich() {
  return (
    <div className="karte">
      <h2>Abgleich mit Firebase Authentication</h2>
      <p className="meldung meldung-warnung">
        Diese Prüfung läuft <strong>nicht</strong> automatisch. „Wie viele
        Konten sind wirklich angemeldet" braucht den Auth-Export, und den sieht
        SQL nicht.
      </p>
      <p className="gedaempft">
        Zuletzt von Hand abgeglichen am <strong>19.08.2026</strong>. Ergebnis
        damals: 15 Konten in der Datenbank ohne Auth-Konto, und{' '}
        <span className="zahl">is_anonymous</span> war bei 744 Konten falsch —
        in beide Richtungen.
      </p>
      <p className="leise">
        Das Datum wird von Hand gepflegt. Es steht hier, damit niemand die
        Prüfung für erledigt hält, nur weil der Rest der Seite grün ist.
      </p>
    </div>
  )
}
